/**
 * FRONTIER F9-4 — The visible, honest adaptation control.
 *
 * Adaptation is transparent, not silent magic: this TopBar affordance lets the
 * operator SEE that the nav/dashboard reordered to their usage, understand WHY
 * (a plain-language explanation of the honest signal + the entitlement bound),
 * and RESET to the default ordering or opt out entirely.
 *
 * When there is no signal yet (cold-start) the trigger reads "Default order" so
 * the UI never implies a personalization it hasn't earned.
 */

import * as React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Switch,
  Separator,
} from '@tyndo/ui-kit';
import { Sparkles, RotateCcw } from 'lucide-react';
import type { AdaptiveShell } from './useAdaptiveShell';

interface AdaptiveControlProps {
  adaptive: AdaptiveShell;
}

/**
 * Compact TopBar control. Uses the shared adaptive facade so the trigger label,
 * the reset action, and the opt-out toggle all reflect the same live state.
 */
export default function AdaptiveControl({ adaptive }: AdaptiveControlProps) {
  const { active, optedOut, interactions, reset, setOptedOut } = adaptive;

  const triggerLabel = active ? 'Adapted to you' : 'Default order';

  return (
    // Modal: the trigger declares aria-haspopup="dialog", so the opened surface
    // must honor the modal-dialog contract (focus trapped inside, outside
    // interaction blocked, aria-modal announced) — WCAG 4.1.2 / ARIA APG.
    <Popover modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-haspopup="dialog"
          className="h-7 gap-1.5 px-2.5 text-xs font-medium"
          data-testid="adaptive-control"
        >
          <Sparkles
            className={`h-3.5 w-3.5 ${active ? 'text-primary' : 'text-muted-foreground'}`}
            aria-hidden="true"
          />
          <span className="hidden md:inline">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" aria-modal="true" className="w-80 p-4 text-sm">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p className="font-medium text-foreground">Personalized layout</p>
        </div>

        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {active ? (
            <>
              Your navigation and dashboard are ordered by how often you open each
              area in this workspace. Frequently-used areas surface first; nothing
              is hidden — every area you can access stays reachable.
            </>
          ) : optedOut ? (
            <>
              Adaptation is turned off. Your navigation and dashboard use the
              default order. Turn it back on to surface your most-used areas first.
            </>
          ) : (
            <>
              Once you start using areas, this workspace will surface your
              most-used ones first. Until then it shows the default order — no
              guessing.
            </>
          )}
        </p>

        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground/80">
          Personalization only reorders areas you already have access to. It can
          never reveal anything your permissions don&apos;t allow.
        </p>

        <Separator className="my-3" />

        <div className="flex items-center justify-between gap-3">
          <label htmlFor="adaptive-optin" className="text-xs font-medium text-foreground">
            Personalize my layout
          </label>
          <Switch
            id="adaptive-optin"
            checked={!optedOut}
            onCheckedChange={(checked: boolean) => setOptedOut(!checked)}
            aria-label="Personalize my layout"
          />
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            {interactions > 0
              ? `${interactions} interaction${interactions === 1 ? '' : 's'} learned`
              : 'No usage learned yet'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={interactions === 0}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset to default
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
